/**
 * Discovery / Time-Travel (Innovation #2) - 2026-05-29.
 *
 * Immutable daily posture snapshots + diff. "What did our PII posture look
 * like N months ago, and what changed?" - asset-count trend, findings-by-
 * severity trend, and which PII classifications appeared / disappeared.
 *   • POST /v1/discovery/snapshots          (take snapshot now)
 *   • GET  /v1/discovery/snapshots          (timeline)
 *   • GET  /v1/discovery/snapshots/diff     (delta between two dates)
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import {
  History, Camera, Loader2, AlertTriangle, ArrowRight, TrendingUp, TrendingDown,
  Database, Plus, Minus, Radar,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { KpiCard } from '@/components/viz/KpiCard';
import {
  listDiscoverySnapshots,
  takeDiscoverySnapshot,
  diffDiscoverySnapshots,
  runRediscovery,
  type DiscoverySnapshot,
  type SnapshotDiff,
  type RediscoveryResult,
} from '@/lib/api';

function sevTotal(s: DiscoverySnapshot): number {
  return Object.values(s.findingCountBySeverity).reduce((a, b) => a + b, 0);
}

export function DiscoveryTimeTravel() {
  const [snapshots, setSnapshots] = useState<DiscoverySnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [taking, setTaking] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [diff, setDiff] = useState<SnapshotDiff | null>(null);
  const [diffing, setDiffing] = useState(false);
  const [rediscovering, setRediscovering] = useState(false);
  const [rediscovery, setRediscovery] = useState<RediscoveryResult | null>(null);

  const refresh = useCallback((signal?: AbortSignal) =>
    listDiscoverySnapshots()
      .then((res) => {
        if (signal?.aborted) return;
        setSnapshots(res.snapshots);
        setError(null);
        if (res.snapshots.length >= 2) {
          setToDate((t) => t || res.snapshots[0]!.snapshotDate);
          setFromDate((f) => f || res.snapshots[res.snapshots.length - 1]!.snapshotDate);
        }
      })
      .catch((err) => { if (!signal?.aborted) setError(err instanceof Error ? err.message : 'Failed to load snapshots'); })
      .finally(() => { if (!signal?.aborted) setLoading(false); }), []);

  useEffect(() => {
    const ctrl = new AbortController();
    refresh(ctrl.signal);
    return () => ctrl.abort();
  }, [refresh]);

  async function onTake() {
    setTaking(true);
    try {
      await takeDiscoverySnapshot();
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to take snapshot');
    } finally {
      setTaking(false);
    }
  }

  async function onRediscover() {
    setRediscovering(true);
    setRediscovery(null);
    try {
      const res = await runRediscovery();
      setRediscovery(res.rediscovery);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to run rediscovery');
    } finally {
      setRediscovering(false);
    }
  }

  async function onDiff() {
    if (!fromDate || !toDate) return;
    setDiffing(true);
    setDiff(null);
    try {
      const res = await diffDiscoverySnapshots(fromDate, toDate);
      setDiff(res.diff);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to diff snapshots');
    } finally {
      setDiffing(false);
    }
  }

  const latest = snapshots[0];
  const labelDeltas = useMemo(
    () => (diff ? Object.entries(diff.classificationLabelDelta).filter(([, v]) => v.delta !== 0).sort((a, b) => Math.abs(b[1].delta) - Math.abs(a[1].delta)) : []),
    [diff],
  );

  return (
    <PageContainer>
      <PageHeader
        title="Posture history"
        subtitle="Immutable posture snapshots (time-travel) - see how your PII footprint changed over time."
        badges={[
          { label: `${snapshots.length} snapshots`, variant: 'glass' },
          ...(loading ? [{ label: 'Loading', variant: 'glass' as const }]
            : error ? [{ label: 'Degraded', variant: 'warning' as const }]
              : [{ label: 'Live', variant: 'success' as const }]),
        ]}
      />

      <GlassCard className="p-5 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-sm"><History className="h-4 w-4 text-primary" /> Posture timeline</CardTitle>
            <p className="text-[11px] text-muted-foreground mt-1">One immutable snapshot per day. Take one now, then compare any two points.</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={onTake} disabled={taking} variant="glass" className="gap-1.5">
              {taking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
              {taking ? 'Snapshotting…' : 'Take snapshot now'}
            </Button>
            <Button onClick={onRediscover} disabled={rediscovering} className="gap-1.5">
              {rediscovering ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radar className="h-4 w-4" />}
              {rediscovering ? 'Rediscovering…' : 'Run rediscovery'}
            </Button>
          </div>
        </div>
        {rediscovery && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            className={
              'mt-3 flex items-start gap-2 rounded-md border px-3 py-2 text-xs ' +
              (rediscovery.driftDetected ? 'border-warning/40 glass-lite' : 'border-success/40 glass-lite')
            }
          >
            <Radar className={'h-4 w-4 shrink-0 mt-0.5 ' + (rediscovery.driftDetected ? 'text-warning' : 'text-success')} />
            <span>
              Rediscovery {rediscovery.snapshotDate}: <strong>{rediscovery.assetCount}</strong> assets.{' '}
              {rediscovery.driftDetected ? (
                <>
                  Drift detected - <strong>{rediscovery.assetCountDelta >= 0 ? '+' : ''}{rediscovery.assetCountDelta}</strong> assets,{' '}
                  <strong>{rediscovery.criticalDelta >= 0 ? '+' : ''}{rediscovery.criticalDelta}</strong> critical
                  {rediscovery.addedLabels.length > 0 && <> · new PII: {rediscovery.addedLabels.join(', ')}</>}
                  {rediscovery.alerted && <> · <strong>alert sent</strong></>}.
                </>
              ) : (
                <>No drift vs the prior snapshot.</>
              )}
            </span>
          </motion.div>
        )}
      </GlassCard>

      {error && !loading && (
        <div className="glass-lite border border-warning/40 rounded-md p-3 mb-3 text-xs flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
          <div className="flex-1"><div className="font-medium">Something went wrong</div><div className="text-muted-foreground mt-0.5">{error}</div></div>
        </div>
      )}

      {!loading && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KpiCard label="Snapshots" value={snapshots.length} icon={History} />
            <KpiCard label="Latest assets" value={latest?.assetCount ?? 0} icon={Database} />
            <KpiCard label="Latest findings" value={latest ? sevTotal(latest) : 0} icon={AlertTriangle} />
            <KpiCard label="PII categories" value={latest ? Object.keys(latest.classificationByLabel).length : 0} icon={TrendingUp} />
          </div>

          {/* Diff comparator */}
          {snapshots.length >= 2 && (
            <GlassCard className="p-5 mb-6">
              <CardHeader className="p-0 mb-3"><CardTitle className="text-sm">Compare two points in time</CardTitle></CardHeader>
              <div className="flex flex-wrap items-end gap-3">
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">From
                  <select value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="mt-1 block glass-lite rounded px-2 py-1.5 text-xs bg-transparent text-foreground">
                    {snapshots.map((s) => <option key={s.id} value={s.snapshotDate}>{s.snapshotDate}</option>)}
                  </select>
                </label>
                <ArrowRight className="h-4 w-4 text-muted-foreground mb-2" />
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">To
                  <select value={toDate} onChange={(e) => setToDate(e.target.value)} className="mt-1 block glass-lite rounded px-2 py-1.5 text-xs bg-transparent text-foreground">
                    {snapshots.map((s) => <option key={s.id} value={s.snapshotDate}>{s.snapshotDate}</option>)}
                  </select>
                </label>
                <Button onClick={onDiff} disabled={diffing} variant="glass" size="sm" className="gap-1.5">
                  {diffing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />} Compare
                </Button>
              </div>

              {diff && (
                <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="mt-4 space-y-3">
                  <div className="flex flex-wrap gap-4 text-sm">
                    <span className="flex items-center gap-1.5">
                      Assets:
                      <strong className={diff.assetCountDelta >= 0 ? 'text-success' : 'text-sev-high'}>
                        {diff.assetCountDelta >= 0 ? '+' : ''}{diff.assetCountDelta}
                      </strong>
                      {diff.assetCountDelta >= 0 ? <TrendingUp className="h-3.5 w-3.5 text-success" /> : <TrendingDown className="h-3.5 w-3.5 text-sev-high" />}
                      <span className="text-[10px] text-muted-foreground">({diff.from?.assetCount ?? 0} → {diff.to.assetCount})</span>
                    </span>
                  </div>
                  {diff.addedLabels.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 text-xs">
                      <Plus className="h-3.5 w-3.5 text-success" /> New PII categories:
                      {diff.addedLabels.map((l) => <Badge key={l} variant="success" className="text-[10px]">{l}</Badge>)}
                    </div>
                  )}
                  {diff.removedLabels.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 text-xs">
                      <Minus className="h-3.5 w-3.5 text-muted-foreground" /> Gone (existed then, not now):
                      {diff.removedLabels.map((l) => <Badge key={l} variant="glass" className="text-[10px]">{l}</Badge>)}
                    </div>
                  )}
                  {labelDeltas.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {labelDeltas.map(([label, v]) => (
                        <div key={label} className="glass-lite rounded-md px-2 py-1.5 text-xs flex items-center justify-between">
                          <span className="font-mono">{label}</span>
                          <span className={v.delta >= 0 ? 'text-success' : 'text-sev-high'}>{v.delta >= 0 ? '+' : ''}{v.delta}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}
            </GlassCard>
          )}

          {/* Timeline table */}
          <GlassCard className="p-0 overflow-hidden">
            <div className="grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/40">
              <div className="col-span-3">Date</div>
              <div className="col-span-2 text-right">Assets</div>
              <div className="col-span-2 text-right">Findings</div>
              <div className="col-span-5">Top PII categories</div>
            </div>
            {snapshots.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                No snapshots yet. Click <strong>Take snapshot now</strong> to capture today's posture.
              </div>
            ) : snapshots.map((s, i) => (
              <motion.div key={s.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.02, 0.3) }}
                className="grid grid-cols-12 items-center px-4 py-3 text-sm border-b border-border/30 hover:bg-muted/40 transition-colors">
                <div className="col-span-3 font-mono text-xs">{s.snapshotDate}</div>
                <div className="col-span-2 text-right tabular text-xs">{s.assetCount.toLocaleString('en-IN')}</div>
                <div className="col-span-2 text-right tabular text-xs">{sevTotal(s).toLocaleString('en-IN')}</div>
                <div className="col-span-5 flex flex-wrap gap-1">
                  {Object.entries(s.classificationByLabel).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([l, c]) => (
                    <Badge key={l} variant="glass" className="text-[9px]">{l}·{c}</Badge>
                  ))}
                </div>
              </motion.div>
            ))}
          </GlassCard>
        </>
      )}
    </PageContainer>
  );
}
